<template>
    <div class="container-fluid px-0">

        <AuthNav></AuthNav>

        <NavButtons light-btns></NavButtons>

    </div>
    <div v-show="currentView === 'login'" class="row">
        <div ref="loginForm" class="col-sm-8 offset-sm-2 col-md-6 offset-md-3 col-xl-4 offset-xl-4 mb-3">
            <div class="d-flex flex-column gap-3 text-center mb-5">
                <h3 class="fw-semi-bold m-0">Login</h3>
                <p class="text-secondary m-0">Welcome! Use the form below to sign into your Account.</p>
            </div>
            <div class="d-flex align-items-center justify-content-between rounded-pill border py-2 px-3 input-container border-active-darken my-3" :class="formErrors.username ? 'border-danger' : 'border-secondary'">
                <label for="username">
                    <i class="me-3 dd-icon bi bi-person-lock"></i>
                </label>
                <input v-model="username" @input="removeFormErrors" type="text" id="username" name="username" class="form-control m-0 border-0 fs-5 p-0 lh-fixed" placeholder="Email / Username" >
            </div>
            <div v-if="formErrors.username" class="validation-error" v-html="formErrors.username"></div>

            <div class="d-flex align-items-center justify-content-between rounded-pill border py-2 px-3 input-container border-active-darken my-3" :class="formErrors.password ? 'border-danger' : 'border-secondary'">
                <label for="password">
                    <i class="me-3 dd-icon bi bi-shield-lock"></i>
                </label>
                <input v-model="password" :type="passwordToggle ? 'text' : 'password'" id="password" name="password" class="form-control m-0 border-0 fs-5 p-0 lh-fixed" placeholder="Password" @input="removeFormErrors" @keyup.enter="login">
                <span class="rounded-circle" v-ripple>
                    <i @click="(event) => passwordToggle = !passwordToggle" :class="passwordToggle ? 'bi-eye' : 'bi-eye-slash'" v-ripple></i>
                </span>
            </div>
            <div v-if="formErrors.password" class="validation-error" v-html="formErrors.password"></div>
            <a v-load-page="'/forgot-password'" class="d-block validation-error text-end text-muted cursor-pointer" :class="formErrors.password ? 'mt-1' : ''">Forgot Password ?</a>

<!--            <div class="d-flex align-items-center justify-content-start py-2 px-3 input-container my-1">-->
<!--                <input v-model="trusted_device" class="form-check-input mt-0 ms-2" id="trusted_device" type="checkbox" name="trusted_device" value="true">-->
<!--                <label class="form-check-label cursor-pointer p-2 ms-3" for="trusted_device">-->
<!--                    Add to Trusted Device-->
<!--                </label>-->
<!--            </div>-->

            <div class="d-flex flex-column align-items-center justify-content-between text-center mt-3">
                <button class="btn btn-dark rounded-pill d-flex align-items-center justify-content-center py-2 px-3 w-50" @click="login" :disabled="hasFormErrors">
                    Login <i class="bi bi-arrow-right"></i>
                </button>
                <div class="mt-3">
                    <span class="text-secondary">Don't have an account?</span>
                    <a v-load-page="'/register'" class="text-primary ms-2 cursor-pointer">Register</a>
                </div>
            </div>

        </div>

    </div>

	<div v-if="currentView === 'verification'">
		<Verification :header="true" :allow_recovery_code="true" :type="twoFactorMode" from="login" :mfa_email="verificationEmail" :user_id="userId" :purpose="purpose" :timer="timer" @close-verification="closeVerification" @confirmation-status="verificationCheck"></Verification>
	</div>

	<PostEmailVerificationModal v-if="showRecoveryModal" :email="username" :uid="username" @close-modal="closeRecoveryModal"></PostEmailVerificationModal>
	<TrustedDeviceModal v-if="showTrustedDeviceModal" :email="username" :user_id="userId"  @close-modal="closeTrustedDeviceModal"></TrustedDeviceModal>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {useAuthStore} from "@/stores/auth-store";
import {useAppHelpers} from "@/composables/useAppHelpers";
import {useHttpErrorManager} from "@/composables/useHttpErrorManager";
import NavButtons from "@/components/NavButtons.vue";
import AuthNav from "@/views/Auth/AuthNav.vue";
import apiClient, { webClient } from "@/utils/axios";
import {useRouter} from "vue-router";
import { useToast } from "@/composables/useToast.js";
import Verification from "@/components/GenericModalAndViews/Verification.vue";
import PostEmailVerificationModal from "@/components/Auth/PostEmailVerificationModal.vue";
import TrustedDeviceModal from "@/components/Auth/TrustedDeviceModal.vue";
import { getCsrfToken } from "@/utils/csrf.js";

const helper = useAppHelpers();
const auth = useAuthStore();
const router = useRouter();
const { displayToast } = useToast();

const loginForm = ref(null);
const currentView = ref('login');
const verificationEmail = ref(null);
const userId = ref(null);
const purpose = ref('login');
const timer = ref(300);

const username = ref('');
const password = ref('');
const trusted_device = ref(false);
const passwordToggle = ref(false);
const formErrors = ref({});

const twoFactorMode = ref('');

const expiredTrustedDevice = ref(false);

const accountSetupComplete = computed(() => auth?.user?.account_setup_completed);

const login = async () => {
    helper.loaderOn(loginForm.value);
    if (!username.value) {
        formErrors.value.username = 'Please enter your Username.';
        helper.loaderOff(loginForm.value);
        return;
    }

    if (!password.value) {
        formErrors.value.password = 'Please enter your Password.';
        helper.loaderOff(loginForm.value);
        return;
    }

    try {
        const response = await apiClient.post('/login', {
            username: username.value,
            password: password.value
        });

        if (response.status >= 200 && response.status < 300) {
			if(response.status === 202 && response.data.email_verification_pending){
				verificationEmail.value = response.data.verification_email;
				timer.value = response.data.expire_seconds;
				displayToast(response.data.message, 'error');
				twoFactorMode.value = 'email';
				purpose.value = 'email_verification';
				currentView.value = 'verification';
				helper.loaderOff(loginForm.value);
			}
			else if(response.status === 203 && response.data.two_factor_required){
				verificationEmail.value = null;
				userId.value = response.data.user_id;
					twoFactorMode.value = response.data.two_factor_method;

				timer.value = twoFactorMode.value === 'email' ? response.data.expire_seconds : 0;
				expiredTrustedDevice.value = response.data.expired_trusted_device ? response.data.expired_trusted_device : false;

				purpose.value = 'two_factor';
				currentView.value = 'verification';
				helper.loaderOff(loginForm.value);
			}
			else{
				auth.CSRF_UPDATING(true);
				const csrfResponse = await webClient.get("/sanctum/csrf-cookie");
				await auth.FETCH_USER_DATA(true);
				await getCsrfToken();

				if(purpose.value === 'email_verification'){
					helper.loaderOn(loginForm.value);
					showRecoveryModal.value = true;
				}
				else{
					helper.loaderOff(loginForm.value);
					await navigateAfterLogin();
				}
				// displayToast(response.data.message, 'outline-success');
			}
        }
    } catch (error) {
        helper.loaderOff(loginForm.value);
        let errorObj = await useHttpErrorManager().handleError(error);
        if (error.response.status === 422) {
            formErrors.value = errorObj;
	        removeFormErrorKey('full_message');
            removeFormErrorKey('alt_message');
            removeFormErrorKey('err_message');
        }
    }
};

const navigateAfterLogin = async () => {
	if(accountSetupComplete.value){
		await router.push({ name: 'home' });
	}
	else{
		await router.push({ name: 'setup_account' });
	}
}

const removeFormErrors = () => {
	removeFormErrorKey('email');
    removeFormErrorKey('username');
    removeFormErrorKey('password');
}

const removeFormErrorKey = (key) => {
    delete formErrors.value[key];
};

const hasFormErrors = computed(() => {
    return Object.keys(formErrors.value).length > 0;
});

const showRecoveryModal = ref(false);
const showTrustedDeviceModal = ref(false);
const verificationCheck = async (val) => {
	currentView.value = 'login';
	if(val && val[0]){
		purpose.value = 'login';
		await login();
	}
}

const closeVerification = () => {
	currentView.value = 'login';
	displayToast({heading: 'Verification Incomplete', message: 'You have not completed the verification. You will prompted again to verify your email address on next login.'}, 'outline-info');
}

const closeRecoveryModal = async () => {
	showRecoveryModal.value = false;
	helper.loaderOff(loginForm.value);
	if(!expiredTrustedDevice.value){
		helper.loaderOn(loginForm.value);
		showTrustedDeviceModal.value = true;
	}
	else {
		await navigateAfterLogin();
	}
}

const closeTrustedDeviceModal = async () => {
	currentView.value = 'login';
	showTrustedDeviceModal.value = false;
	helper.loaderOff(loginForm.value);
	await navigateAfterLogin();
}

onMounted(() => {
});
</script>

<style scoped lang="scss"></style>
